import clsx from "clsx";

type Props = {
  /** Ordered step labels, one per step. */
  steps: string[];
  /** The step the form is on, or "complete" once it has been accepted. */
  current: number | "complete";
  /** Accessible name for the nav. */
  label?: string;
};

/**
 * The port of fragments/partials/steps-nav.php: the progress nav for a stepped
 * form, rendered as a sibling of the form's body rather than inside it. The
 * body is replaced wholesale by the form's success state once a submission is
 * accepted, and the nav has to survive that in order to move to its last item.
 *
 * The "Complete" item is not one of the form's steps and is never declared by
 * one. Any form with more than a single step ends by completing, so the item is
 * appended here - a form that is not stepped gets no nav at all.
 *
 * The items carry no href and no button: this reports where you are, it is not
 * a way of getting there. Steps are moved between with the form's own
 * controls, which is what keeps a step from being skipped past its validation.
 *
 * The nav reports three things and the form is the source of all of them
 * (syncNav() in form.js): which step you are on, which you have been through,
 * and whether you are finished. Each item says which with data-state, active
 * or done, and its mark and label style themselves from it.
 *
 * form.js also set aria-disabled on the nav once the form was complete; the
 * navigation role does not support it, and the nav has nothing to disable.
 */
/** An item, and the rule that runs from it to the next one. */
const itemClass =
  "group/step flex items-center gap-2 not-last:after:mx-2.5 not-last:after:h-px not-last:after:w-[clamp(20px,4vw,56px)] not-last:after:bg-brand-blue/8 not-last:after:content-['']";

/** The circle that holds an item's number, or its tick. */
const markClass = clsx(
  "flex size-6.5 shrink-0 items-center justify-center rounded-full border-[1.5px] border-brand-blue/18",
  "transition-[background-color,border-color] duration-400 ease-[ease]",
  "group-data-[state=active]/step:border-brand-blue group-data-[state=active]/step:bg-brand-blue",
  "group-data-[state=done]/step:border-das-green group-data-[state=done]/step:bg-das-green",
);

/** On a phone only the item you are on keeps its label. */
const labelClass = clsx(
  "text-[12px] leading-[1.3] font-normal whitespace-nowrap text-brand-blue/38",
  "group-data-[state=active]/step:font-semibold group-data-[state=active]/step:text-brand-blue group-data-[state=done]/step:text-das-green",
  "mobile:hidden mobile:group-data-[state=active]/step:block",
);

export default function FormSteps({
  steps,
  current,
  label = "Form progress",
}: Props) {
  // One step is a form, not a journey; there is nothing to report the progress of.
  if (steps.length < 2) {
    return null;
  }

  const complete = current === "complete";

  return (
    <nav className="m-0 mb-10 mobile:mb-8 js-form-steps" aria-label={label}>
      <ol className="m-0 flex list-none items-center p-0">
        {steps.map((step, index) => {
          const number = index + 1;
          const done =
            complete || (typeof current === "number" && number < current);

          return (
            <li
              key={number}
              className={itemClass}
              data-step={number}
              data-state={
                number === current ? "active" : done ? "done" : undefined
              }
            >
              <span className={markClass} aria-hidden="true">
                <span className="text-[9.5px] leading-none font-bold tracking-[0.04em] text-brand-blue/32 group-data-[state=active]/step:text-white group-data-[state=done]/step:hidden">
                  {String(number).padStart(2, "0")}
                </span>

                <Tick className="hidden text-white group-data-[state=done]/step:block" />
              </span>

              <span className={labelClass}>{step}</span>
            </li>
          );
        })}

        {/*
         * The end of every stepped form. It carries the tick in place of a
         * number because it is never a step you are asked to fill in - it is
         * only ever somewhere you arrive. The tick shows from the start, in
         * outline while the item is still ahead and filled once it is reached.
         */}
        <li
          className={itemClass}
          data-step="complete"
          data-state={complete ? "active" : undefined}
        >
          <span className={markClass} aria-hidden="true">
            <Tick
              className={clsx(
                "block",
                complete ? "text-white" : "text-brand-blue/32",
              )}
            />
          </span>

          <span className={labelClass}>Complete</span>
        </li>
      </ol>
    </nav>
  );
}

function Tick({ className }: { className: string }) {
  return (
    <svg
      className={className}
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      focusable="false"
    >
      <path
        d="M2 5.5l2.5 2.5 4.5-4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
